import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import useCreditLimitData from './useCreditLimitData';
import { formatCurrency } from '../../../utils/NumberHelper';
import Entity from '../../../models/Entity';
import { useSSEEntityEvent } from '../../../services/sse/queries';
import { events } from 'enums/sse';

const CreditLimitContext = createContext(null);

const useCreditLimitContext = () => useContext(CreditLimitContext);

interface CreditLimitProviderProps {
  children: ReactNode;
  maxMonitorClients: number;
  requestMeData: () => void;
  onClose: () => void;
  totalClientsCount: number;
  user: typeof Entity;
}

const CreditLimitProvider = ({
  children,
  maxMonitorClients,
  onClose,
  requestMeData,
  totalClientsCount,
  user,
}: CreditLimitProviderProps) => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState(false);
  const [selectedClientsAmount, setSelectedClientsAmount] = useState<number | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [membershipUpgraded, setMembershipUpgraded] = useState(false);
  const chargebeeInstance = useRef(null);
  const [timer, setTimer] = useState(null);

  const { monthlyPlanSinglePrice, handlePlanUpgrade } = useCreditLimitData(chargebeeInstance);

  const onStepBack = () => {
    setStep((prevState) => (prevState === 1 ? prevState : prevState - 1));
  };

  const getFlexiPlanClientsPrice = (packages: number) =>
    formatCurrency((packages * monthlyPlanSinglePrice) / 100);

  const topUpVariants = {
    20: getFlexiPlanClientsPrice(1),
    30: getFlexiPlanClientsPrice(2),
    40: getFlexiPlanClientsPrice(3),
    50: getFlexiPlanClientsPrice(4),
  };

  const handleContinueToOrderButton = () => setStep(2);

  const onMembershipUpgraded = () => {
    clearTimeout(timer);
    requestMeData?.();
    setPaymentLoading(false);
    setMembershipUpgraded(true);
  };

  useEffect(() => {
    if (paymentSuccess && paymentLoading) {
      const timeoutTimer = setTimeout(() => {
        if (!membershipUpgraded) {
          setPaymentLoading(false);
          setError(true);
        }
      }, 30000);
      setTimer(timeoutTimer);
    }
  }, [membershipUpgraded, paymentLoading, paymentSuccess]);

  useSSEEntityEvent({
    user,
    eventType: events.membershipUpgraded,
    finishCallback: onMembershipUpgraded,
  });

  const contextValue = {
    chargebeeInstance,
    error,
    handleContinueToOrderButton,
    handlePlanUpgrade,
    maxMonitorClients,
    onClose,
    onStepBack,
    paymentLoading,
    paymentSuccess,
    selectedClientsAmount,
    setError,
    setPaymentLoading,
    setPaymentSuccess,
    setSelectedClientsAmount,
    setStep,
    step,
    topUpVariants,
    totalClientsCount,
  };

  return <CreditLimitContext.Provider value={contextValue}>{children}</CreditLimitContext.Provider>;
};

export { CreditLimitProvider, useCreditLimitContext };
